import React, { useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { Certificate, ZerosslOrderState } from '../../../types/models/Certificate';
import type { ApiResponseCertificateDeleted } from '../../../types/api/ApiResponseCertificate';

interface DeleteZerosslCertificateModalProps {
  certificate: Certificate;
  /** Drives the wording: `pending` reads as "cancel the order", terminal states as "delete". */
  orderState: ZerosslOrderState;
  onClose: () => void;
  /** The row is gone — remove it from the list / navigate away from the detail page. */
  onDeleted: (certificateId: number) => void;
}

/**
 * Confirmation for DELETE /datacenters/certificates/{id} — the cleanup action
 * for ZeroSSL orders that are not issued: cancels the order upstream (best
 * effort), removes the auto-created DNS validation CNAMEs, and drops the
 * local certificate + order rows. Issued certificates are not deletable
 * (they hold the only copy of the private key); the backend's atomic guard
 * turns a delete racing the moment of issuance into a JSend error, which is
 * surfaced verbatim here.
 */
function DeleteZerosslCertificateModal({ certificate, orderState, onClose, onDeleted }: DeleteZerosslCertificateModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isCancel = orderState === 'pending';
  const actionLabel = isCancel ? 'Cancel order' : 'Delete';
  const title = isCancel ? 'Cancel certificate order' : 'Delete certificate order';

  const notifyFailure = (description: string) => {
    addNotification({
      title: isCancel ? 'Could not cancel the certificate order' : 'Could not delete the certificate order',
      srTitle: 'Error alert',
      description,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      const response = await apiFetch(`/datacenters/certificates/${certificate.id}`, { method: 'DELETE' });
      const body = (await response.json().catch(() => null)) as ApiResponseCertificateDeleted | null;
      if (body?.status === 'success' && body.data?.deleted) {
        addNotification({
          title: isCancel ? 'Certificate order cancelled' : 'Certificate order deleted',
          srTitle: 'Success alert',
          description: `The order for ${certificate.cn} was ${isCancel ? 'cancelled' : 'deleted'} and its validation records were cleaned up.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onDeleted(certificate.id);
        onClose();
        return;
      }
      // Includes the atomic-guard race ("the order completed while the
      // deletion was being processed") and the 409 for already-issued rows.
      const message = body?.message ?? `Request failed with status ${response.status} ${response.statusText}`.trim();
      setError(message);
      notifyFailure(message);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      notifyFailure(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal variant="small" isOpen onClose={onClose} aria-label={title}>
      <ModalHeader title={title} titleIconVariant="warning" />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title={isCancel ? 'Could not cancel the order' : 'Could not delete the order'} className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content component="p">
          {isCancel ? (
            <>
              This cancels the pending order for <strong>{certificate.cn}</strong> on ZeroSSL, removes any
              automatically created DNS validation records, and deletes the pending certificate entry.
            </>
          ) : (
            <>
              This deletes the {orderState} order for <strong>{certificate.cn}</strong>, cleans up any remaining DNS
              validation records, and removes the certificate entry. You can place a new order afterwards.
            </>
          )}
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleDelete()} isLoading={isDeleting} isDisabled={isDeleting}>
          {actionLabel}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isDeleting}>
          {isCancel ? 'Keep order' : 'Cancel'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default DeleteZerosslCertificateModal;
